import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./FundsWithdrawalPage.css";

const DEFAULT_PAGE_LIMIT = 20;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "Withdrawal History";

const TABS = [
  { key: "PENDING", label: "Pending", status: "Pending" },
  { key: "COMPLETED", label: "Completed", status: "Completed" },
  { key: "REJECTED", label: "Rejected", status: "Rejected" },
];

const truncateHash = (str, head = 8, tail = 6) => {
  if (!str || str.length <= head + tail) return str || "—";
  return `${str.slice(0, head)}...${str.slice(-tail)}`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const parseAmount = (amount) => {
  if (amount == null) return null;
  if (typeof amount === "number") return amount;
  if (typeof amount === "object" && amount.$numberDecimal != null) {
    return Number(amount.$numberDecimal);
  }
  const n = Number(amount);
  return Number.isNaN(n) ? null : n;
};

const formatNum = (val) => {
  const n = parseAmount(val);
  if (n == null) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 12 });
};

const userFullName = (user) => {
  if (!user || typeof user !== "object") return "—";
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const getFeeExport = (row) => {
  if (row?.withdrawalFee != null && row.withdrawalFee !== "") {
    const n = parseAmount(row.withdrawalFee);
    return n != null ? n : String(row.withdrawalFee);
  }
  const fee = row?.fee;
  if (!fee) return "";
  if (typeof fee === "object" && fee.fee_used != null) return fee.fee_used;
  return String(fee);
};

const rowToExport = (row) => {
  const user = row.userId;
  return {
    "Created At": row.createdAt ?? "",
    "User Name": userFullName(user) === "—" ? "" : userFullName(user),
    UUID: user?.uuid ?? "",
    Email: user?.emailId ?? "",
    Mobile: user?.mobileNumber ?? "",
    "User ID": user?._id ?? user?.id ?? "",
    Coin: row.coin ?? "",
    "Token ID": row.tokenId ?? "",
    Chain: row.chainId ?? "",
    Amount: parseAmount(row.amount) ?? row.amount ?? "",
    Fee: getFeeExport(row),
    "To Address": row.toAddress ?? "",
    "Tx Hash": row.txHash ?? "",
    Confirmations: row.confirmedNum ?? "",
    "Confirmation Threshold": row.confirmingThreshold ?? "",
    Status: row.status ?? "",
    "Sub Status": row.subStatus ?? "",
    "Failed Reason": row.failedReason ?? "",
    "Refund Done": row.refundDone ? "Yes" : "No",
    "Webhook Succeeded": row.webhookSucceeded ? "Yes" : "No",
    "Completed At": row.completedAt ?? "",
    "Request ID": row.request_id ?? "",
    "Transaction ID": row.transactionId ?? "",
    "Record ID": row._id ?? "",
  };
};

const statusClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "rejected" || s === "cancelled" || s === "failed") return "rejected";
  if (s === "completed" || s === "complete") return "completed";
  if (s === "pending" || s === "confirming") return "pending";
  return s;
};

const CopyHashCell = ({ value, head = 8, tail = 6, onCopy }) => {
  if (!value) return <span className="withdrawal-hash">—</span>;
  return (
    <div className="withdrawal-address-cell">
      <div className="withdrawal-tooltip-wrap">
        <span className="withdrawal-hash withdrawal-tooltip-wrap__trigger">
          {truncateHash(value, head, tail)}
        </span>
        <div className="withdrawal-tooltip-box" role="tooltip">
          {value}
        </div>
      </div>
      <button
        type="button"
        className="withdrawal-copy-btn"
        onClick={() => onCopy(value)}
        title="Copy"
        aria-label="Copy"
      >
        <i className="fas fa-copy" />
      </button>
    </div>
  );
};

const FundsWithdrawalPage = () => {
  const [activeTab, setActiveTab] = useState("PENDING");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [viewDetail, setViewDetail] = useState(null);
  const [toast, setToast] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [userId, setUserId] = useState("");
  const [coin, setCoin] = useState("");
  const [chainId, setChainId] = useState("");
  const [txHashFilter, setTxHashFilter] = useState("");

  const [actionModal, setActionModal] = useState(null);
  const [rejectedReason, setRejectedReason] = useState("");
  const [approvalMode, setApprovalMode] = useState("auto");
  const [adminTransactionHash, setAdminTransactionHash] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");

  const tabConfig = useMemo(
    () => TABS.find((t) => t.key === activeTab) || TABS[0],
    [activeTab]
  );

  useEffect(() => {
    setPage(1);
    setSearch("");
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
  }, [userId, coin, chainId, txHashFilter, pageLimit]);

  const buildParams = useCallback(
    (pageNum = page, limitNum = pageLimit) => {
      const params = { page: pageNum, limit: limitNum, status: tabConfig.status };
      if (userId.trim()) params.userId = userId.trim();
      if (coin.trim()) params.coin = coin.trim();
      if (chainId.trim()) params.chainId = chainId.trim();
      if (txHashFilter.trim()) params.txHash = txHashFilter.trim();
      return params;
    },
    [page, pageLimit, tabConfig.status, userId, coin, chainId, txHashFilter]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.getCoboWithdrawalHistory(buildParams());
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
      setTotalPages(res?.pagination?.totalPages || res?.pagination?.total_pages || 1);
      setTotalCount(res?.pagination?.total ?? list.length);
      if (res?.success === false && !list.length) {
        setError(res?.message || "Failed to load withdrawals.");
      }
      window.dispatchEvent(new Event("dashboardCountsUpdated"));
    } catch {
      setRows([]);
      setTotalPages(1);
      setTotalCount(0);
      setError("Failed to load withdrawals.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const user = row.userId;
      const fields = [
        row._id,
        row.request_id,
        row.transactionId,
        row.txHash,
        row.coin,
        row.chainId,
        row.toAddress,
        row.status,
        user?._id,
        user?.uuid,
        user?.emailId,
        user?.mobileNumber,
        user?.firstName,
        user?.lastName,
      ];
      return fields.some((f) => f != null && String(f).toLowerCase().includes(q));
    });
  }, [rows, search]);

  const showTxHashCol = activeTab !== "PENDING";
  const tableColSpan = 13;

  const openApprove = (row) => {
    setActionModal({ row, type: "Approved" });
    setRejectedReason("");
    setApprovalMode("auto");
    setAdminTransactionHash("");
    setActionError("");
  };

  const openReject = (row) => {
    setActionModal({ row, type: "Rejected" });
    setRejectedReason("");
    setApprovalMode("auto");
    setAdminTransactionHash("");
    setActionError("");
  };

  const closeActionModal = () => {
    if (actionLoading) return;
    setActionModal(null);
    setRejectedReason("");
    setApprovalMode("auto");
    setAdminTransactionHash("");
    setActionError("");
  };

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      setToast({ type: "success", message: "Copied to clipboard." });
      setTimeout(() => setToast(null), 2000);
    } catch {
      setToast({ type: "error", message: "Failed to copy." });
      setTimeout(() => setToast(null), 2000);
    }
  };

  const fetchAllFilteredWithdrawals = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getCoboWithdrawalHistory(
        buildParams(currentPage, EXPORT_BATCH)
      );
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.totalPages || res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllFilteredWithdrawals();
      if (!allRows.length) {
        setToast({ type: "error", message: "No withdrawals match your filters to export." });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const exportData = allRows.map(rowToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);

      const tabSlug = tabConfig.key.toLowerCase();
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `withdrawal_${tabSlug}_${dateStamp}.xlsx`);

      setToast({
        type: "success",
        message: `Exported ${allRows.length} withdrawal(s) to Excel.`,
      });
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      console.error(e);
      setToast({
        type: "error",
        message: e?.message || "Failed to export Excel file.",
      });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageLimitChange = (e) => {
    setPageLimit(Number(e.target.value) || DEFAULT_PAGE_LIMIT);
    setPage(1);
  };

  const pageStart = filtered.length === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = filtered.length === 0 ? 0 : (page - 1) * pageLimit + filtered.length;

  const handleConfirmAction = async () => {
    if (!actionModal?.row) return;
    const { row, type } = actionModal;

    if (type === "Approved" && approvalMode === "manual" && !adminTransactionHash.trim()) {
      setActionError("Transaction hash is required for manual approval.");
      return;
    }

    setActionLoading(true);
    setActionError("");

    const payload = {
      adminApproved: type,
      withdrawalId: row._id,
    };
    if (type === "Rejected" && rejectedReason.trim()) {
      payload.rejectedReason = rejectedReason.trim();
    }
    if (type === "Approved" && approvalMode === "manual") {
      payload.approvalMode = "manual";
      payload.adminTransactionHash = adminTransactionHash.trim();
    }

    const res = await AdminService.updateCoboWithdrawal(payload);
    setActionLoading(false);

    if (res?.success) {
      setToast({
        type: "success",
        message:
          type === "Approved"
            ? "Withdrawal approved successfully."
            : "Withdrawal rejected successfully.",
      });
      closeActionModal();
      fetchRows();
      setTimeout(() => setToast(null), 4000);
    } else {
      setActionError(res?.message || "Failed to update withdrawal status.");
    }
  };

const getFeeDisplay = (row) => {
  if (row?.withdrawalFee != null && row.withdrawalFee !== "") {
    return formatNum(row.withdrawalFee);
  }
  const fee = row?.fee;
  if (!fee) return "—";
  if (typeof fee === "object" && fee.fee_used != null) return fee.fee_used;
  return String(fee);
};

const getFeeExport = (row) => {
  if (row?.withdrawalFee != null && row.withdrawalFee !== "") {
    const n = parseAmount(row.withdrawalFee);
    return n != null ? n : String(row.withdrawalFee);
  }
  const fee = row?.fee;
  if (!fee) return "";
  if (typeof fee === "object" && fee.fee_used != null) return fee.fee_used;
  return String(fee);
};

const rowToExport = (row) => {
  const user = row.userId;
  return {
    "Created At": row.createdAt ?? "",
    "User Name": userFullName(user) === "—" ? "" : userFullName(user),
    UUID: user?.uuid ?? "",
    Email: user?.emailId ?? "",
    Mobile: user?.mobileNumber ?? "",
    "User ID": user?._id ?? user?.id ?? "",
    Coin: row.coin ?? "",
    "Token ID": row.tokenId ?? "",
    Chain: row.chainId ?? "",
    Amount: parseAmount(row.amount) ?? row.amount ?? "",
    Fee: getFeeExport(row),
    "To Address": row.toAddress ?? "",
    "Tx Hash": row.txHash ?? "",
    Confirmations: row.confirmedNum ?? "",
    "Confirmation Threshold": row.confirmingThreshold ?? "",
    Status: row.status ?? "",
    "Sub Status": row.subStatus ?? "",
    "Failed Reason": row.failedReason ?? "",
    "Refund Done": row.refundDone ? "Yes" : "No",
    "Webhook Succeeded": row.webhookSucceeded ? "Yes" : "No",
    "Completed At": row.completedAt ?? "",
    "Request ID": row.request_id ?? "",
    "Transaction ID": row.transactionId ?? "",
    "Record ID": row._id ?? "",
  };
};

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Funds Withdrawal</span>
          </p>
          <h1 className="dashboard-hero__title">Funds Withdrawal</h1>
          <p className="dashboard-hero__subtitle">
            Process and track Cobo withdrawal requests
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-arrow-up" />
        </div>
      </section>

      {toast && (
        <div className={`withdrawal-toast withdrawal-toast--${toast.type}`} role="status">
          <i className={`fas fa-${toast.type === "success" ? "check-circle" : "exclamation-circle"}`} />
          {toast.message}
        </div>
      )}

      <div className="dashboard-body">
        <section className="withdrawal-section">
          <div className="withdrawal-section__header">
            <div className="withdrawal-section__title-wrap">
              <span className="withdrawal-section__icon">
                <i className="fas fa-money-bill-wave" />
              </span>
              <h2 className="withdrawal-section__title">Withdrawal Requests</h2>
            </div>
            <div className="withdrawal-section__actions">
              <span className="withdrawal-stat">
                <strong>{filtered.length}</strong> on page
                {totalCount != null && (
                  <>
                    {" "}
                    (<strong>{totalCount}</strong> total)
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="withdrawal-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="withdrawal-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered withdrawals as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
              <button
                type="button"
                className="withdrawal-refresh-btn"
                onClick={fetchRows}
                disabled={loading}
                title="Refresh"
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="withdrawal-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`withdrawal-tab ${activeTab === tab.key ? "withdrawal-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            className="withdrawal-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <input
              type="text"
              placeholder="Search on page (UUID, email, hash, coin…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="withdrawal-search-input"
              style={{ flex: "1 1 220px", maxWidth: "none" }}
            />
            <input
              type="text"
              placeholder="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="withdrawal-search-input"
              style={{ flex: "1 1 160px", maxWidth: "none" }}
            />
            <input
              type="text"
              placeholder="Coin (USDT…)"
              value={coin}
              onChange={(e) => setCoin(e.target.value)}
              className="withdrawal-search-input"
              style={{ flex: "1 1 120px", maxWidth: "none" }}
            />
            <input
              type="text"
              placeholder="Chain ID"
              value={chainId}
              onChange={(e) => setChainId(e.target.value)}
              className="withdrawal-search-input"
              style={{ flex: "1 1 140px", maxWidth: "none" }}
            />
            <input
              type="text"
              placeholder="Tx hash"
              value={txHashFilter}
              onChange={(e) => setTxHashFilter(e.target.value)}
              className="withdrawal-search-input"
              style={{ flex: "1 1 180px", maxWidth: "none" }}
            />
            <label className="withdrawal-page-limit">
              <span>Rows per page</span>
              <select
                value={pageLimit}
                onChange={handlePageLimitChange}
                className="withdrawal-search-input withdrawal-page-limit__select"
                disabled={loading}
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {error && (
            <p className="withdrawal-empty withdrawal-empty--error" role="alert">
              {error}
            </p>
          )}

          <div className="withdrawal-table-wrap">
            <table className="withdrawal-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>User</th>
                  <th>UUID</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Coin</th>
                  <th>Chain</th>
                  <th>Amount</th>
                  <th>Fee</th>
                  <th>To Address</th>
                  {showTxHashCol && <th>Tx Hash</th>}
                  <th>Confirmations</th>
                  <th>Status</th>
                  <th>View</th>
                  {activeTab === "PENDING" && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={tableColSpan} className="withdrawal-table__loading">
                      <i className="fas fa-spinner fa-spin" /> Loading withdrawals…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className="withdrawal-table__loading">
                      No withdrawals found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => {
                    const user = row.userId;
                    const uid = user?._id || user?.id;
                    return (
                      <tr key={row._id}>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>{userFullName(user)}</td>
                        <td>
                          {uid ? (
                            <Link
                              to={`/dashboard/trader/${uid}`}
                              className="withdrawal-mono withdrawal-user-id"
                              style={{ textDecoration: "none" }}
                            >
                              {user?.uuid ?? "—"}
                            </Link>
                          ) : (
                            user?.uuid ?? "—"
                          )}
                        </td>
                        <td>{user?.emailId ?? "—"}</td>
                        <td>{user?.mobileNumber ?? "—"}</td>
                        <td>
                          <strong>{row.coin ?? "—"}</strong>
                        </td>
                        <td>{row.chainId ?? "—"}</td>
                        <td>{formatNum(row.amount)}</td>
                        <td>{getFeeDisplay(row)}</td>
                        <td>
                          <CopyHashCell value={row.toAddress} onCopy={handleCopy} />
                        </td>
                        {showTxHashCol && (
                          <td>
                            <CopyHashCell
                              value={row.txHash}
                              head={10}
                              tail={8}
                              onCopy={handleCopy}
                            />
                          </td>
                        )}
                        <td>
                          {row.confirmedNum != null
                            ? `${row.confirmedNum}/${row.confirmingThreshold ?? "—"}`
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`withdrawal-status withdrawal-status--${statusClass(row.status)}`}
                          >
                            {row.status ?? "—"}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="withdrawal-btn withdrawal-btn--view"
                            onClick={() => setViewDetail(row)}
                            title="View details"
                          >
                            <i className="fas fa-eye" /> View
                          </button>
                        </td>
                        {activeTab === "PENDING" && (
                          <td>
                            <div className="withdrawal-actions">
                              <button
                                type="button"
                                className="withdrawal-btn withdrawal-btn--approve"
                                onClick={() => openApprove(row)}
                              >
                                <i className="fas fa-check" /> Approve
                              </button>
                              <button
                                type="button"
                                className="withdrawal-btn withdrawal-btn--cancel"
                                onClick={() => openReject(row)}
                              >
                                <i className="fas fa-times" /> Reject
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!loading && totalCount > 0 && (
            <div className="withdrawal-pagination">
              <button
                type="button"
                className="withdrawal-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>
              <span className="withdrawal-page-info">
                Showing {pageStart}–{pageEnd} of {totalCount}
                {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
              </span>
              <button
                type="button"
                className="withdrawal-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>

      {actionModal && (
        <div className="withdrawal-modal-overlay" onClick={closeActionModal}>
          <div className="withdrawal-modal withdrawal-modal--action" onClick={(e) => e.stopPropagation()}>
            <div className="withdrawal-modal__header">
              <h3>
                {actionModal.type === "Approved" ? "Approve Withdrawal" : "Reject Withdrawal"}
              </h3>
              <button
                type="button"
                className="withdrawal-modal__close"
                onClick={closeActionModal}
                aria-label="Close"
                disabled={actionLoading}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="withdrawal-modal__body">
              <dl className="withdrawal-dl withdrawal-dl--compact">
                <div>
                  <dt>Withdrawal ID</dt>
                  <dd className="withdrawal-mono">{actionModal.row._id || "—"}</dd>
                </div>
                <div>
                  <dt>Request ID</dt>
                  <dd className="withdrawal-mono">{actionModal.row.request_id || "—"}</dd>
                </div>
                <div>
                  <dt>User</dt>
                  <dd>
                    {userFullName(actionModal.row.userId)} ({actionModal.row.userId?.emailId})
                  </dd>
                </div>
                <div>
                  <dt>Amount</dt>
                  <dd>
                    {formatNum(actionModal.row.amount)} {actionModal.row.coin}
                  </dd>
                </div>
                <div>
                  <dt>To address</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {actionModal.row.toAddress || "—"}
                  </dd>
                </div>
              </dl>

              {actionModal.type === "Approved" && (
                <div className="withdrawal-approval-mode">
                  <p className="withdrawal-approval-mode__label">Approval mode</p>
                  <div className="withdrawal-approval-mode__options">
                    <label className="withdrawal-approval-mode__option">
                      <input
                        type="radio"
                        name="approvalMode"
                        value="auto"
                        checked={approvalMode === "auto"}
                        onChange={() => {
                          setApprovalMode("auto");
                          setAdminTransactionHash("");
                          setActionError("");
                        }}
                        disabled={actionLoading}
                      />
                      <span>
                        <strong>Auto</strong>
                        <small>Send approval to Cobo (default flow)</small>
                      </span>
                    </label>
                    <label className="withdrawal-approval-mode__option">
                      <input
                        type="radio"
                        name="approvalMode"
                        value="manual"
                        checked={approvalMode === "manual"}
                        onChange={() => {
                          setApprovalMode("manual");
                          setActionError("");
                        }}
                        disabled={actionLoading}
                      />
                      <span>
                        <strong>Manual</strong>
                        <small>Mark approved with your on-chain transaction hash</small>
                      </span>
                    </label>
                  </div>
                  {approvalMode === "auto" ? (
                    <p className="withdrawal-metamask__hint">
                      This will send an approval request to Cobo for processing this withdrawal.
                    </p>
                  ) : (
                    <label className="withdrawal-form-label">
                      Transaction hash <span className="withdrawal-required">*</span>
                      <input
                        type="text"
                        className="withdrawal-form-input"
                        placeholder="Enter on-chain transaction hash"
                        value={adminTransactionHash}
                        onChange={(e) => setAdminTransactionHash(e.target.value)}
                        disabled={actionLoading}
                      />
                    </label>
                  )}
                </div>
              )}

              {actionModal.type === "Rejected" && (
                <label className="withdrawal-form-label">
                  Rejected reason (optional)
                  <textarea
                    className="withdrawal-form-input"
                    rows={3}
                    placeholder="Enter reason for rejection..."
                    value={rejectedReason}
                    onChange={(e) => setRejectedReason(e.target.value)}
                    disabled={actionLoading}
                  />
                </label>
              )}

              {actionError && (
                <p className="withdrawal-action-error" role="alert">
                  {actionError}
                </p>
              )}

              <div className="withdrawal-modal__footer">
                <button
                  type="button"
                  className="withdrawal-btn withdrawal-btn--ghost"
                  onClick={closeActionModal}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`withdrawal-btn ${
                    actionModal.type === "Approved"
                      ? "withdrawal-btn--approve"
                      : "withdrawal-btn--cancel"
                  }`}
                  onClick={handleConfirmAction}
                  disabled={
                    actionLoading ||
                    (actionModal.type === "Approved" &&
                      approvalMode === "manual" &&
                      !adminTransactionHash.trim())
                  }
                >
                  {actionLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Processing…
                    </>
                  ) : actionModal.type === "Approved" ? (
                    "Confirm Approve"
                  ) : (
                    "Confirm Reject"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewDetail && (
        <div className="withdrawal-modal-overlay" onClick={() => setViewDetail(null)}>
          <div className="withdrawal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="withdrawal-modal__header">
              <h3>Withdrawal Details</h3>
              <button
                type="button"
                className="withdrawal-modal__close"
                onClick={() => setViewDetail(null)}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="withdrawal-modal__body">
              <dl className="withdrawal-dl">
                <div>
                  <dt>Record ID</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">{viewDetail._id || "—"}</dd>
                </div>
                <div>
                  <dt>Request ID</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">{viewDetail.request_id || "—"}</dd>
                </div>
                <div>
                  <dt>Transaction ID</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">{viewDetail.transactionId || "—"}</dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatDate(viewDetail.createdAt)}</dd>
                </div>
                <div>
                  <dt>Completed At</dt>
                  <dd>{formatDate(viewDetail.completedAt)}</dd>
                </div>
                <div>
                  <dt>User</dt>
                  <dd>{userFullName(viewDetail.userId)}</dd>
                </div>
                <div>
                  <dt>UUID</dt>
                  <dd className="withdrawal-mono">{viewDetail.userId?.uuid ?? "—"}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{viewDetail.userId?.emailId ?? "—"}</dd>
                </div>
                <div>
                  <dt>Mobile</dt>
                  <dd>{viewDetail.userId?.mobileNumber ?? "—"}</dd>
                </div>
                <div>
                  <dt>Coin</dt>
                  <dd>{viewDetail.coin ?? "—"}</dd>
                </div>
                <div>
                  <dt>Token ID</dt>
                  <dd>{viewDetail.tokenId ?? "—"}</dd>
                </div>
                <div>
                  <dt>Chain</dt>
                  <dd>{viewDetail.chainId ?? "—"}</dd>
                </div>
                <div>
                  <dt>Amount</dt>
                  <dd>{formatNum(viewDetail.amount)}</dd>
                </div>
                <div>
                  <dt>Fee</dt>
                  <dd>{getFeeDisplay(viewDetail)}</dd>
                </div>
                <div>
                  <dt>To Address</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail.toAddress || "—"}
                  </dd>
                </div>
                <div>
                  <dt>Tx Hash</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail.txHash || "—"}
                  </dd>
                </div>
                <div>
                  <dt>Confirmations</dt>
                  <dd>
                    {viewDetail.confirmedNum != null
                      ? `${viewDetail.confirmedNum} / ${viewDetail.confirmingThreshold ?? "—"}`
                      : "—"}
                  </dd>
                </div>
                {viewDetail.subStatus && (
                  <div>
                    <dt>Sub Status</dt>
                    <dd>{viewDetail.subStatus}</dd>
                  </div>
                )}
                {viewDetail.failedReason && (
                  <div>
                    <dt>Failed Reason</dt>
                    <dd>{viewDetail.failedReason}</dd>
                  </div>
                )}
                <div>
                  <dt>Refund Done</dt>
                  <dd>{viewDetail.refundDone ? "Yes" : "No"}</dd>
                </div>
                <div>
                  <dt>Webhook Succeeded</dt>
                  <dd>{viewDetail.webhookSucceeded ? "Yes" : "No"}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    <span
                      className={`withdrawal-status withdrawal-status--${statusClass(viewDetail.status)}`}
                    >
                      {viewDetail.status ?? "—"}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default FundsWithdrawalPage;
